import { sql } from 'drizzle-orm'
import {
  bigint,
  boolean,
  check,
  index,
  integer,
  jsonb,
  pgEnum,
  pgTable,
  smallint,
  text,
  timestamp,
} from 'drizzle-orm/pg-core'

import { user } from './auth.ts'
import { createdAt, primaryId, taxRateBps, updatedAt } from './columns.ts'
import { deliveryMethod } from './delivery.ts'
import { city } from './geo.ts'
import { lead } from './leads.ts'
import { offering, offeringUnit, offeringVariant } from './offerings.ts'
import { orderItem } from './orders.ts'

/**
 * How much of a cancelled reservation comes back (spec §10 · I2).
 *
 * A CEILING rather than a state: what a customer actually gets depends on when
 * they cancel, which nothing writes down as it happens. This column only ever
 * moves DOWNWARD, and the one thing that moves it is a reschedule — see
 * `tierAfterReschedule` in `@repo/core`. Without it, moving a Friday booking
 * on Thursday evening would buy back a full refund that was already gone.
 */
export const bookingRefundTier = pgEnum('booking_refund_tier', ['FULL', 'HALF', 'NONE'])

export const bookingStatus = pgEnum('booking_status', [
  'PENDING',
  'CONFIRMED',
  'ACTIVE',
  'COMPLETED',
  'CANCELLED',
  'NO_SHOW',
])

/**
 * A dated reservation of anything with a calendar: a camera for the weekend, a
 * room for three hours, an operator for a shoot day.
 *
 * **One table.** It replaced `rental_booking` and `studio_booking`, which had
 * diverged into two spellings of the same reservation — inclusive `date` +
 * `days` on one, half-open `timestamp` + `hours` on the other — and would have
 * needed a third the moment production got a calendar. The panel's calendar,
 * the availability check and "moje rezerwacje" are each one query now instead
 * of a union of two.
 *
 * **The period is half-open: `[startsAt, endsAt)`.** The old rental table used
 * inclusive dates, which made a same-day hire one day and an inverted range an
 * empty interval that overlaps nothing — precisely the shape that slips past
 * overlap detection. Half-open makes "ends where the next one starts" mean
 * exactly that, and is the precondition for the `btree_gist` EXCLUDE constraint
 * this table still owes (see below).
 *
 * Both `orderItemId` and `leadId` are nullable and both directions happen: a
 * booking can be born from a paid basket line, or from an enquiry a human later
 * confirms. The customer fields are a snapshot, because a reservation is a
 * named agreement with a specific person and must stay readable after the
 * account it came from is anonymised.
 *
 * **Overlap IS enforced at the database level, for equipment** (spec §11 · I5).
 * `booking_unit_no_overlap` is a `btree_gist` EXCLUDE over `(unit_id, [blocked
 * period))` that skips cancelled and no-show rows. It is written in raw SQL in
 * the migration rather than here, because drizzle-kit has no EXCLUDE builder —
 * the schema cannot express it, so this comment is where it is recorded.
 *
 * It excludes on the BLOCKED window, not the paid one: spec §11's buffers are
 * days the gear is genuinely unavailable (courier both ways, a check-over
 * between hires), so two hires whose buffers meet are two hires that cannot
 * both happen.
 *
 * Rooms and shoot days still have no `unit_id`, so the constraint does not
 * reach them — for those, `assertFree` plus the checkout transaction remain the
 * only guard, and that is recorded here so nobody mistakes the gap for
 * completeness.
 */
export const booking = pgTable(
  'booking',
  {
    id: primaryId('bkg'),
    offeringId: text('offering_id')
      .notNull()
      .references(() => offering.id, { onDelete: 'restrict' }),
    /**
     * WHICH physical copy is out (spec §11 · I5).
     *
     * NULL for everything that is not a numbered thing — a room, a shoot day,
     * an enquiry nobody has allocated yet. Set for equipment, and it is what
     * `booking_unit_no_overlap` excludes on: the constraint this table has
     * owed since it was written could not exist against a stock COUNT, only
     * against an identity.
     *
     * `RESTRICT`, like `offering_id`: a unit with hires against it is not a row
     * to delete, it is a camera with a history. Retiring one is
     * `offering_unit.is_active`.
     */
    unitId: text('unit_id').references(() => offeringUnit.id, { onDelete: 'restrict' }),
    /** Which variant was booked — `doba`, `pół dnia`. NULL for a bare enquiry. */
    offeringVariantId: text('offering_variant_id').references(() => offeringVariant.id, {
      onDelete: 'set null',
    }),
    orderItemId: text('order_item_id').references(() => orderItem.id, { onDelete: 'set null' }),
    leadId: text('lead_id').references(() => lead.id, { onDelete: 'set null' }),
    userId: text('user_id').references(() => user.id, { onDelete: 'set null' }),
    /**
     * WHO from the team this hour belongs to — the photographer on the plan,
     * the operator on the shoot, the strategist in the meeting.
     *
     * `user_id` above is the CUSTOMER, and until this column existed there was
     * no way to ask "is Kasia free on Friday at ten?": every reservation was
     * attached to an OFFERING, so two photographers were one counter
     * (`stock_quantity`) and a meeting in somebody's diary was invisible to the
     * calendar that assigned them a shoot.
     *
     * NULL for everything with nobody on it — a room somebody hired, a camera
     * on a shelf, an enquiry nobody has allocated. It is deliberately NOT the
     * only way a reservation finds its performer: one born from a paid line
     * carries `order_item_id`, and `order_item.assigned_user_id` answers the
     * same question for every row written before this column existed. The
     * timeline query reads BOTH, which is why no backfill was needed.
     *
     * `SET NULL` on delete, like `user_id`: a leaver's shoot days are history
     * of what the shop did, not rows to remove.
     */
    performerUserId: text('performer_user_id').references(() => user.id, { onDelete: 'set null' }),
    /** Where the work happens, for offerings that travel. NULL when irrelevant. */
    cityId: text('city_id').references(() => city.id, { onDelete: 'set null' }),

    status: bookingStatus('status').notNull().default('PENDING'),

    // ── customer snapshot ─────────────────────────────────────────────────
    customerFirstName: text('customer_first_name'),
    customerLastName: text('customer_last_name'),
    customerEmail: text('customer_email').notNull(),
    customerPhone: text('customer_phone'),
    customerAddress: text('customer_address'),

    // ── period ────────────────────────────────────────────────────────────
    startsAt: timestamp('starts_at', { withTimezone: true }).notNull(),
    endsAt: timestamp('ends_at', { withTimezone: true }).notNull(),
    /**
     * When the "your reservation is tomorrow" notice went out (spec §18 · L2).
     *
     * A column and not a lookup in the notification log, for the same reason
     * `order_item.escalated_at` is one: the sweep needs a predicate it can put
     * in a WHERE clause and an index can answer, and "has this booking ever
     * been the subject of a BOOKING_REMINDER row" is neither. NULL means the
     * reminder is still owed, which is also what a rescheduled booking should
     * go back to — see `rescheduleBooking`.
     */
    reminderSentAt: timestamp('reminder_sent_at', { withTimezone: true }),
    /**
     * Whole days rather than a clock window — an equipment hire or a shoot day.
     * The times are still real timestamps; this says to render and reason about
     * them as dates, so a rental never displays "09:00–09:00".
     */
    isAllDay: boolean('is_all_day').notNull().default(false),
    /**
     * What this reservation takes out of the CALENDAR — the paid window plus
     * its unpaid buffer (spec §10 · I1).
     *
     * `[blockedFrom, blockedTo)` contains `[startsAt, endsAt)` and is what
     * every overlap test compares. Two columns rather than a buffer length,
     * because the overlap is an index-usable range predicate on them and a
     * length would make it arithmetic in the WHERE clause — and because the
     * calendar has to DRAW the blocked band, which needs the instants anyway.
     *
     * Equal to the paid window for everything with no buffer, which is
     * everything except a studio today. See `bufferMinutesFor` in `@repo/core`.
     */
    blockedFrom: timestamp('blocked_from', { withTimezone: true }).notNull(),
    blockedTo: timestamp('blocked_to', { withTimezone: true }).notNull(),
    /**
     * The number the price was built from, in the offering's own unit: days for
     * a hire, hours for a room. May exceed the wall-clock span when a minimum
     * booking applies.
     */
    billedUnits: integer('billed_units').notNull(),
    /** How many identical stock units this reservation holds. */
    quantity: integer('quantity').notNull().default(1),

    // ── money, frozen at booking time ─────────────────────────────────────
    /** NET grosze per billed unit. NULL while an enquiry is unpriced. */
    unitPriceNetMinor: bigint('unit_price_net_minor', { mode: 'number' }),
    discountBps: smallint('discount_bps').notNull().default(0),
    totalNetMinor: bigint('total_net_minor', { mode: 'number' }),
    depositNetMinor: bigint('deposit_net_minor', { mode: 'number' }),
    /**
     * Frozen VAT rate, for the same reason every other priced row has one
     * (`columns.ts`): a rate change must not rewrite the tax on a reservation
     * somebody already agreed to. A booking born from a LEAD has no
     * `order_item` to inherit it from, so without this column there is nowhere
     * for its rate to live at all.
     */
    taxRateBps: taxRateBps(),

    // ── delivery and the hand-over clock (spec §11 · I6) ─────────────────
    /**
     * How this hire travels. NULL for everything that is not equipment.
     *
     * Copied onto the booking from the basket's choice rather than joined back
     * to the order, because the two facts it decides live HERE: the unpaid
     * buffer is already baked into `blocked_from`/`blocked_to`, and the return
     * deadline is measured from `delivered_at` below. A join would put the
     * reason a day is blocked one table away from the block.
     */
    deliveryMethod: deliveryMethod('delivery_method'),
    /**
     * When the customer actually got it — the hour they walked out of the
     * office, or InPost's "Dostarczone".
     *
     * ONE column for both methods, deliberately. Spec §11 gives them different
     * words but every rule downstream asks the same question of both — when
     * does the twenty-four hours start — and two columns would be two places
     * for the answer to be missing.
     *
     * Set BY HAND today. There is no InPost account and therefore no webhook;
     * Support types the delivery time from the app. Recorded here rather than
     * left as a surprise: the column does not care who wrote it, and the day an
     * integration lands it writes the same column.
     */
    deliveredAt: timestamp('delivered_at', { withTimezone: true }),
    /** When it came back. NULL while it is still out. */
    returnedAt: timestamp('returned_at', { withTimezone: true }),
    /**
     * Spec §11's penalty, frozen: 200 % of the BASE daily rate per day late.
     *
     * Stored rather than derived for the reason `refund_net_minor` is: it is
     * the answer AT THE MOMENT the gear came back, computed from the rate that
     * was current then. NULL while nothing is owed.
     */
    lateFeeNetMinor: bigint('late_fee_net_minor', { mode: 'number' }),

    // ── the first hire is checked by a person (spec §11 · I7) ────────────
    /**
     * This hire waits for a human before the gear goes out.
     *
     * > Pierwszy najem nowego klienta: ręczna akceptacja przez Admina lub
     * > Support przed wydaniem.
     *
     * Written at checkout from the customer's HISTORY, not from the basket —
     * "first" is a fact about a person. It gates the HAND-OVER and nothing
     * else: the order is placed, paid and reserved exactly like anybody
     * else's, which is what makes spec §11's "kurier dozwolony także przy
     * pierwszym najmie" expressible at all.
     */
    requiresApproval: boolean('requires_approval').notNull().default(false),
    /** When somebody said yes. NULL while it is still waiting. */
    approvedAt: timestamp('approved_at', { withTimezone: true }),
    /**
     * Who said yes — kept even after they leave.
     *
     * `SET NULL` rather than `RESTRICT`: the decision stays on the record with
     * the name gone, which is the same trade every other authored column in
     * this schema makes.
     */
    approvedByUserId: text('approved_by_user_id').references(() => user.id, {
      onDelete: 'set null',
    }),

    // ── cancellation and moves (spec §10 · I2) ───────────────────────────
    /**
     * The best refund this reservation may still reach. See
     * {@link bookingRefundTier}: a cap, not a verdict.
     */
    refundTier: bookingRefundTier('refund_tier').notNull().default('FULL'),
    /**
     * How many times the term has been moved. Spec §10 allows exactly one
     * ("maksymalnie 1 przełożenie") and calls the limit protection against
     * abuse — a booking moved freely is a room held indefinitely for nothing.
     */
    rescheduleCount: integer('reschedule_count').notNull().default(0),
    /**
     * What was actually handed back, in NET grosze, once somebody cancelled.
     *
     * Stored rather than derived, because it is the answer AT THE MOMENT of
     * the cancellation: the tier follows from the clock, and re-deriving it a
     * week later would report a different number than the one that was paid.
     * NULL for everything still open.
     */
    refundNetMinor: bigint('refund_net_minor', { mode: 'number' }),

    /**
     * Where a MEETING happens: `ONLINE`, `OFFICE`, or NULL for everything that
     * is not one.
     *
     * The owner's rule is that booking either kind takes the slot away from
     * the other — *„jeśli ktoś zabookuje spotkanie online to stacjonarne
     * strategiczne wypada"* — so this column deliberately does NOT take part in
     * the overlap test. Two places, one calendar: the exclusivity is what
     * `listOverlapping` already does for every row of the same offering, and a
     * place that narrowed it would be a way to double-book an hour by picking
     * the other tile.
     *
     * It is here for the CALENDAR, which draws the two in different colours,
     * and for the confirmation, which has to say whether to turn up or to
     * expect a link. Not a status: `booking_status` is a database enum and a
     * place is not a state of anything (see `bookings/schemas.ts`).
     */
    meetingPlace: text('meeting_place'),

    /**
     * The pricing rules in force when this row was created (Cennik → Reguły).
     *
     * Nullable, and a NULL is not a gap: rows written before the rules became
     * editable were always judged by the rule of the day, and `thawRules`
     * falls through to the live values for exactly that reason. Only the rules
     * applied LATER are stamped here — see `RULES_FROZEN_ON_BOOKING` for which and
     * why the rest are not.
     */
    pricingRulesSnapshot: jsonb('pricing_rules_snapshot').$type<Record<string, number>>(),

    /** What is being shot — the panel calendar's line label. */
    title: text('title'),
    notes: text('notes'),
    confirmedAt: timestamp('confirmed_at', { withTimezone: true }),
    cancelledAt: timestamp('cancelled_at', { withTimezone: true }),

    createdAt: createdAt(),
    updatedAt: updatedAt(),
  },
  (t) => [
    index('booking_offering_idx').on(t.offeringId, t.startsAt, t.endsAt),
    index('booking_status_idx').on(t.status, t.startsAt),
    index('booking_user_idx').on(t.userId),
    index('booking_order_item_idx').on(t.orderItemId),
    // THE read behind every conflict check on a person: "what is in this
    // performer's way between Monday and Sunday?" Over the BLOCKED pair rather
    // than the paid one, because that is the window the test compares — see
    // `listPerformerTimeline`.
    index('booking_performer_idx').on(t.performerUserId, t.blockedFrom, t.blockedTo),

    // A booking is a priced row, so it carries the same guards every priced
    // table in this schema does. Nullable throughout because a `PENDING`
    // enquiry is a request, not a quote — the enquiry form writes one before a
    // human has priced it. So each check is "NULL, or sane", never "present".
    check(
      'booking_unit_price_positive',
      sql`${t.unitPriceNetMinor} IS NULL OR ${t.unitPriceNetMinor} > 0`,
    ),
    check('booking_total_nonneg', sql`${t.totalNetMinor} IS NULL OR ${t.totalNetMinor} >= 0`),
    check(
      'booking_deposit_positive',
      sql`${t.depositNetMinor} IS NULL OR ${t.depositNetMinor} > 0`,
    ),
    check('booking_discount_range', sql`${t.discountBps} >= 0 AND ${t.discountBps} <= 10000`),
    check('booking_tax_rate_range', sql`${t.taxRateBps} >= 0 AND ${t.taxRateBps} <= 10000`),
    // Zero billed units at any rate is a free reservation, and the RENTAL_DAYS
    // ladder reads this column.
    check('booking_units_positive', sql`${t.billedUnits} > 0`),
    check('booking_quantity_positive', sql`${t.quantity} > 0`),
    check('booking_reschedule_count_nonneg', sql`${t.rescheduleCount} >= 0`),
    // An approval is a moment and a person, or neither. Half of it is a record
    // nobody can act on: "approved, by nobody" and "nobody approved it" would
    // read the same and mean opposite things.
    check(
      'booking_approval_complete',
      sql`(${t.approvedAt} IS NULL) = (${t.approvedByUserId} IS NULL)`,
    ),
    check('booking_refund_nonneg', sql`${t.refundNetMinor} IS NULL OR ${t.refundNetMinor} >= 0`),
    check(
      'booking_late_fee_nonneg',
      sql`${t.lateFeeNetMinor} IS NULL OR ${t.lateFeeNetMinor} >= 0`,
    ),
    // Gear cannot come back before it went out. The one sequence that is not a
    // typo but a bug in whatever wrote it.
    check(
      'booking_returned_after_delivered',
      sql`${t.returnedAt} IS NULL OR (${t.deliveredAt} IS NOT NULL AND ${t.returnedAt} >= ${t.deliveredAt})`,
    ),
    // Strict `>`: with a half-open period a zero-length block is not "one
    // hour", it is nothing — and it would overlap nothing, which is how a
    // double-booking sneaks past an overlap check.
    check('booking_period_ordered', sql`${t.endsAt} > ${t.startsAt}`),
    // Two places and nothing else. A free-text column here would be read by
    // the calendar's colour rule and by the confirmation letter, and a third
    // spelling would silently fall through both.
    check(
      'booking_meeting_place_known',
      sql`${t.meetingPlace} IS NULL OR ${t.meetingPlace} IN ('ONLINE', 'OFFICE')`,
    ),
    // The blocked window CONTAINS the paid one. A buffer that fell inside the
    // booking would be an hour sold and then taken back; one that inverted
    // would be a reservation blocking nothing.
    check(
      'booking_blocked_contains_period',
      sql`${t.blockedFrom} <= ${t.startsAt} AND ${t.blockedTo} >= ${t.endsAt}`,
    ),
    // THE overlap read, and it is on the blocked window rather than the paid
    // one because that is what the check actually compares.
    index('booking_blocked_idx').on(t.offeringId, t.blockedFrom, t.blockedTo),
    index('booking_unit_idx').on(t.unitId, t.blockedFrom),
  ],
)

export type Booking = typeof booking.$inferSelect
export type NewBooking = typeof booking.$inferInsert
